import React, { useEffect, useState } from 'react';
import { Autocomplete, FormControl, Grid, InputLabel, MenuItem, Select, TextField, Button, Box, CircularProgress, Alert } from "@mui/material";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { getData, postData } from '../../../services/axiosInstance';
import { API_ENDPOINTS } from '../../../services/endpoints';
import MissingDepartmentsDialog from '../common/MissingDepartmentsDialog';

function CreateEpicPage({ projectData, onCancel, onClose, onSuccess }) {
    const navigate = useNavigate();
    const userDataRaw = localStorage.getItem('userData');
    const userData = userDataRaw ? JSON.parse(userDataRaw) : null;
    const userId = userData?.id;
    const userRole = userData?.user_type;
    const roleBasePath = userRole === 'Team Lead' ? '/team-lead' : '/manager';
    const [projects, setProjects] = useState([]);
    const [productOwnerOptions, setProductOwnerOptions] = useState([]);
    const today = dayjs().startOf('day');
    const [loading, setLoading] = useState({
        projects: false,
        po: false
    });
    const [submitting, setSubmitting] = useState(false);
    const [apiErrorMessage, setApiErrorMessage] = useState("");
    const [showMissingDeptDialog, setShowMissingDeptDialog] = useState(false);
    const [selectedProjectDetails, setSelectedProjectDetails] = useState(null);

    const [form, setForm] = useState({
        title: '',
        projectId: null,
        productOwnerId: null,
        teamLeaderId: null,
        description: '',
        priority: 'Medium',
        status: 'Todo',
        startDate: null,
        endDate: null,
        color: '#6366f1'
    });

    useEffect(() => {
        if (projectData && projectData.id) {
            setForm(prev => ({
                ...prev,
                projectId: projectData.id,
                teamLeaderId: projectData.team_leader_id || projectData.team_leader?.id || null,
                projectStart: projectData.start_date,
                projectEnd: projectData.end_date
            }));
        }
    }, [projectData]);

    useEffect(() => {
        const fetchProjects = async () => {
            if (!userId) return;
            try {
                setLoading(prev => ({ ...prev, projects: true }));
                const res = await getData(API_ENDPOINTS.fetchProjectsBySupervisor(userId));
                if (res.status === "SUCCESS") {
                    const fetchedProjects = res.message.data || [];
                    setProjects(fetchedProjects);
                    if (projectData && !fetchedProjects.find(p => p.id === projectData.id)) {
                        setProjects(prev => [projectData, ...prev]);
                    }
                }
            } catch (error) {
                return null;
            } finally {
                setLoading(prev => ({ ...prev, projects: false }));
            }
        };
        fetchProjects();
    }, [userId, projectData]);

    useEffect(() => {
        const fetchProductOwners = async () => {
            if (!form.projectId) {
                setProductOwnerOptions([]);
                return;
            }

            try {
                setLoading(prev => ({ ...prev, po: true }));
                const res = await getData(API_ENDPOINTS.fetchProductOwnersForProject(form.projectId));
                if (res.status === "SUCCESS" && res.result) {
                    setProductOwnerOptions(res.result);
                } else {
                    setProductOwnerOptions([]);
                }
            } catch (error) {
                setProductOwnerOptions([]);
            } finally {
                setLoading(prev => ({ ...prev, po: false }));
            }
        };

        fetchProductOwners();
    }, [form.projectId]);

    useEffect(() => {
        const fetchProjectDetails = async () => {
            if (!form.projectId) {
                setSelectedProjectDetails(null);
                return;
            }

            try {
                const res = await getData(API_ENDPOINTS.fetchProjectById(form.projectId));
                if (res.status === "SUCCESS") {
                    const projectDetails = res.message.data || null;
                    setSelectedProjectDetails(projectDetails);

                    // Check if departments are empty
                    const hasDepartments = projectDetails?.departments && projectDetails.departments.length > 0;
                    if (!hasDepartments) {
                        setShowMissingDeptDialog(true);
                    }
                }
            } catch (error) {
                // Handle error silently
            }
        };

        fetchProjectDetails();
    }, [form.projectId]);

     const handleRefreshProjectDetails = async () => {
        if (!form.projectId) return;
        try {
            const res = await getData(API_ENDPOINTS.fetchProjectById(form.projectId));
            if (res.status === "SUCCESS") {
                const projectDetails = res.message.data || null;
                setSelectedProjectDetails(projectDetails);

                // Check if departments are now present
                const hasDepartments = projectDetails?.departments && projectDetails.departments.length > 0;
                if (hasDepartments) {
                    setShowMissingDeptDialog(false);
                }
            }
        } catch (error) {
            // Handle error silently
        }
    };

    const handleChange = (field, value, extraData = null) => {
        setForm(prev => {
            const updated = { ...prev, [field]: value };
            if (field === "projectId") {
                updated.productOwnerId = null;
                updated.teamLeaderId = extraData?.team_leader_id || null;
                updated.projectStart = extraData?.start_date;
                updated.projectEnd = extraData?.end_date;
            }
            return updated;
        });
    };

    const handleSubmit = async (e) => {
        setApiErrorMessage("");
        e.preventDefault();
        if (!form.projectId) return;

        try {
            setSubmitting(true);
            const payload = {
                title: form.title,
                manager_id: userId,
                description: form.description,
                product_owner_id: form.productOwnerId,
                team_lead_id: form.teamLeaderId,
                status: form.status,
                priority: form.priority,
                color: form.color,
                start_date: form.startDate ? dayjs(form.startDate).format('YYYY-MM-DD') : null,
                end_date: form.endDate ? dayjs(form.endDate).format('YYYY-MM-DD') : null,
            };
            const res = await postData(`${API_ENDPOINTS.createProjectEpic}/${form.projectId}/epics`, payload);

            if (res.status === "SUCCESS") {
                onSuccess();
                if (projectData) {
                    navigate(`${roleBasePath}/epic-management`);
                }
            }
        } catch (error) {
            const catchErrorMsg = error.response?.data?.message || error.message || "An unexpected error occurred";
            setApiErrorMessage(catchErrorMsg);
        } finally {
            setSubmitting(false);
        }
    };

    const handleCancelClick = () => {
        if (projectData && onClose) {
            onClose();
        } else if (onCancel) {
            onCancel();
        } else {
            navigate(-1);
        }
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12 }}>
                        <TextField
                            label="Epic Name"
                            fullWidth
                            required
                            size="small"
                            value={form.title}
                            onChange={(e) => handleChange("title", e.target.value)}
                        />
                    </Grid>

                    {/* Project Selection */}
                    <Grid size={{ xs: 12, sm: 6 }} >
                        <Autocomplete
                            disabled={!!projectData}
                            options={projects}
                            getOptionLabel={(option) => option.project_title || ""}
                            size="small"
                            loading={loading.projects}
                            value={projects.find(p => p.id === form.projectId) || null}
                            onChange={(_, newValue) => handleChange("projectId", newValue?.id || null, newValue)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Select Project"
                                    required
                                    placeholder={loading.projects ? "Loading..." : ""}
                                />
                            )}
                        />
                    </Grid>

                    {/* Product Owner Selection */}
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Autocomplete
                            options={productOwnerOptions}
                            getOptionLabel={(option) =>
                                option.first_name ? `${option.first_name} ${option.last_name || ''}` : ""
                            }
                            size="small"
                            loading={loading.po}
                            disabled={!form.projectId}
                            value={productOwnerOptions.find(m => m.id === form.productOwnerId) || null}
                            onChange={(_, newValue) => handleChange("productOwnerId", newValue?.id || null)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Product Owner"
                                    required
                                    placeholder={!form.projectId ? "Select a project first" : (loading.po ? "Loading..." : "Select PO")}
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {loading.po ? <CircularProgress color="inherit" size={20} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />
                    </Grid>

                    {/* Status & Priority */}
                    {["status", "priority"].map((f) => (
                        <Grid size={{ xs: 12, sm: 6 }} key={f}>
                            <FormControl
                                fullWidth
                                size="small"
                                disabled={f === "status"}
                            >
                                <InputLabel sx={{ textTransform: 'capitalize' }}>{f}</InputLabel>
                                <Select
                                    label={f}
                                    value={form[f]}
                                    onChange={e => handleChange(f, e.target.value)}
                                >
                                    {(f === "status" ? ["Todo", "In Progress", "Completed"] : ["High", "Medium", "Low"]).map(opt => (
                                        <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    ))}

                    {/* Dates */}
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <DatePicker
                            label="Start Date"
                            sx={{ width: '100%' }}
                            format='DD/MM/YYYY'
                            value={form.startDate}
                            onChange={d => handleChange("startDate", d)}
                            // Disable everything before today
                            minDate={today}
                            // Ensure it doesn't exceed project end if projectEnd exists
                            maxDate={form.projectEnd ? dayjs(form.projectEnd) : null}
                            slotProps={{
                                textField: {
                                    size: 'small',
                                    helperText: form.projectStart ? `Project Start Date: ${dayjs(form.projectStart).format('DD/MM/YYYY')}` : 'Select a project to see timeline'
                                    // helperText: form.projectStart
                                    //     ? `Project Timeline: ${dayjs(form.projectStart).format('DD/MM/YYYY')} - ${dayjs(form.projectEnd).format('DD/MM/YYYY')}`
                                    //     : 'Select a project to see timeline'
                                },
                                day: {
                                    sx: (date) => ({
                                        // Optional: Visually mark the project start date specifically if it's in the past
                                        ...(form.projectStart && dayjs(date).isSame(dayjs(form.projectStart), 'day') && {
                                            border: '1px dashed #6366f1 !important',
                                            borderRadius: '50%',
                                        })
                                    })
                                }
                            }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <DatePicker
                            label="End Date"
                            sx={{ width: '100%' }}
                            format='DD/MM/YYYY'
                            value={form.endDate}
                            onChange={d => handleChange("endDate", d)}
                            // End date must be today or after the start date
                            minDate={form.startDate ? dayjs(form.startDate).add(1, 'day') : today}
                            // End date cannot exceed project end
                            maxDate={form.projectEnd ? dayjs(form.projectEnd) : null}
                            slotProps={{
                                textField: {
                                    size: 'small',
                                    helperText: form.projectEnd ? `Project Deadline: ${dayjs(form.projectEnd).format('DD/MM/YYYY')}` : ''
                                }
                            }}
                        />
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                        <TextField
                            label="Description"
                            fullWidth
                            multiline
                            rows={3}
                            size="small"
                            value={form.description}
                            onChange={(e) => handleChange("description", e.target.value)}
                        />
                    </Grid>

                    <Box my={3}>
                        {apiErrorMessage && (
                            <Alert
                                severity="error"
                                sx={{ mb: 3, borderRadius: '12px', fontWeight: 600 }}
                            >
                                {apiErrorMessage}
                            </Alert>
                        )}
                    </Box>

                    <Grid size={{ xs: 12 }} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                        <Button onClick={handleCancelClick} variant="text" color="inherit" size="small" sx={{ textTransform: 'none' }}>
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={submitting || !form.projectId || !form.productOwnerId}
                            size="small"
                            sx={{ bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' }, textTransform: 'none' }}
                        >
                            {submitting ? 'Creating...' : 'Create Epic'}
                        </Button>
                    </Grid>
                </Grid>
            </Box>

            <MissingDepartmentsDialog
                open={showMissingDeptDialog}
                project={selectedProjectDetails}
                onClose={() => setShowMissingDeptDialog(false)}
                onDepartmentsAdded={handleRefreshProjectDetails}
            />
        </LocalizationProvider>
    );
}

export default CreateEpicPage;